import {ModelSetOptions} from 'backbone';
import $ from 'jquery';

import Trim from 'analyses/alignment/Trim';
import {assertAndContinue} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import * as biojs from 'BioJS';
import {AnnotationAttributes} from 'biojs/Annotation';
import {GAP} from 'biojs/BioConstants';
import GapSequence from 'biojs/GapSequence';
import {PartAttributes} from 'biojs/Part';
import {Casing} from 'biojs/Sequence';
import {SequencePrimerAttributes} from 'biojs/SequencePrimer';
import {TranslationAttributes} from 'biojs/Translation';
import {DoSeqOpReturnPromise, Sequence, SequenceAttributes} from 'components/Sequence';
import {sequenceVersionIdFrom} from 'components/SequenceAttributeCollection';
import {AlignmentPolymerType} from 'constants/AlignmentConstants';
import {Strand} from 'constants/MolbioConstants';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import FileState from 'filestate/FileState';
import {ResidueHighlightFormat} from 'molbio/AlignmentPDFHelpers/AlignmentPDFConstants';
import {getResidueHighlight} from 'molbio/AlignmentUtil';
import Annotation from 'molbio/annotations/backbone/Annotation';
import Enzymes from 'molbio/enzymes/backbone/Enzymes';
import {LineRangePoints, PathBuilder} from 'molbio/maps/linear/PathBuilder';
import * as sequtil from 'molbio/SeqUtil';
import Translation from 'molbio/translations/backbone/Translation';
import {ServerNotePart} from 'notebook/util/NoteUtilConstants';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import * as Net from 'util/Net';
import {assertNever} from 'util/Types';

export interface LineRanges {
  correct: LineRangePoints;
  mutation: LineRangePoints;
  unknown: LineRangePoints;
}

export interface GapSequenceWrapperOptions {
  handleCanEdit: (options?: {isFeatureEdit: boolean}) => boolean;
  isInSync: () => boolean;
  rotationIndex: number;
  trimStart?: number;
  trimEnd?: number;
}

export interface GapSequenceWrapperAttributes extends SequenceAttributes {
  trim?: Trim;
  hasTraceData: boolean;
  baseSequence: Sequence | null;
  _sequence: GapSequence;
}

/**
 * GapSequenceWrapper is a subclass of the components/Sequence model that extends its behavior for
 * the purposes of alignments. Just like how components/Sequence holds its contents in a
 * biojs/Sequence, this class GapSequenceWrapper holds its contents in a GapSequence.
 */
interface GapSequenceWrapper {
  get<K extends keyof GapSequenceWrapperAttributes>(key: K): GapSequenceWrapperAttributes[K];
  set(obj: Partial<GapSequenceWrapperAttributes>, options?: ModelSetOptions): GapSequenceWrapper;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set(attributeName: string, value: any, options?: ModelSetOptions): never;
}
class GapSequenceWrapper extends Sequence {
  lineRanges!: LineRanges | null;
  handleCanEdit!: (options?: {isFeatureEdit: boolean}) => boolean;
  isInSync!: () => boolean;
  rotationIndex!: number;

  /**
   * Wrap a baseSequence-editing promise to show a loading screen while the sequence is being edited and
   * signal to the analysis that the sequence versionId should be updated. Maintains same contract as
   * components/sequence:Sequence edit operations (with an additional promise attached to the returned promise
   * called `client` indicating when rendering has finished).
   *
   * Note: The caller is expected to (a) pass in a baseSequence promise directly (not wrapped by $.when) and
   * (b) perform the necessary edits to the analysis to keep it in sync with the latest sequence version.
   *
   * @param {jqXHR} sequenceXHR - a promise that resolves when the corresponding base sequence is updated, or
   *                              immediately if there is no such sequence (i.e. for a non-first alignment row)
   * @return {jqXHR} - a promise that resolves when the edit finishes, with a second promise `client` that
   *                   indicates when the client has re-rendered.
   */
  wrapForSequenceXHR(sequenceXHR: JQueryPromise<unknown>): DoSeqOpReturnPromise {
    LoadingScreen.show('Updating sequence');
    sequenceXHR.always(() => {
      LoadingScreen.hide();
    });
    sequenceXHR.done(() => {
      const versionId = Net.isJqXHR(sequenceXHR) ? sequenceVersionIdFrom(sequenceXHR) : null;
      if (versionId != null) {
        this.get('_sequence').trigger('sequenceVersionUpdate', {versionId});
      }
    });

    const xhr: DoSeqOpReturnPromise = $.when(sequenceXHR) as DoSeqOpReturnPromise;
    xhr.client = xhr;
    return xhr;
  }

  _computeLineRanges(masterSequence: GapSequence): LineRanges {
    return GapSequenceWrapper.computeLineRanges(
      this.get('_sequence').bases(),
      masterSequence.bases(),
      this.get('trim')
    );
  }

  static computeLineRanges(
    residues: string,
    masterResidues: string,
    trim?: Trim | null,
    alignmentPolymerType: AlignmentPolymerType = AlignmentPolymerType.DNA
  ): LineRanges {
    const correctPathBuilder = new PathBuilder();
    const mutationPathBuilder = new PathBuilder();
    const unknownValuesPathBuilder = new PathBuilder();

    const sameLength = residues.length === masterResidues.length;
    assertAndContinue(
      sameLength,
      `Aligned sequence ${residues.length} has different length than master sequence ${masterResidues.length}. Possibly computing line ranges too early.`
    );
    if (!sameLength) {
      return {correct: [], mutation: [], unknown: []};
    }

    for (let index = 0; index < residues.length; index++) {
      // Determine which path builder will receive the index.
      const residue = residues[index];
      let targetPathBuilder = null;
      if (trim != null && !(trim.start() <= index && index < trim.end())) {
        // show as gap
      } else {
        const residueHighlight = getResidueHighlight(masterResidues, residues, index, alignmentPolymerType);
        switch (residueHighlight) {
          case null: {
            if (residue !== GAP) {
              targetPathBuilder = correctPathBuilder;
            } else {
              // show as gap
            }
            break;
          }
          case ResidueHighlightFormat.UNKNOWN: {
            targetPathBuilder = unknownValuesPathBuilder;
            break;
          }
          case ResidueHighlightFormat.MUTATION: {
            targetPathBuilder = mutationPathBuilder;
            break;
          }
          default: {
            assertNever(residueHighlight);
          }
        }
      }
      if (targetPathBuilder != null) {
        targetPathBuilder.addIndex(index);
      }
    }

    return {
      correct: correctPathBuilder.getPoints(),
      mutation: mutationPathBuilder.getPoints(),
      unknown: unknownValuesPathBuilder.getPoints(),
    };
  }

  invalidateLineRanges(): void {
    this.lineRanges = null;
  }

  getLineRanges(masterSequence: GapSequence): LineRanges {
    if (this.lineRanges == null) {
      this.lineRanges = this._computeLineRanges(masterSequence);
    }
    return this.lineRanges;
  }

  initialize(
    models: {permissions: Permissions; _sequence: GapSequence},
    options: GapSequenceWrapperOptions
  ): void {
    super.initialize();
    // Set sequence permissions separately because the alignment endpoint does not return up-to-date sequence
    // permissions, and permissions don't change from the sequence view.
    if (models.permissions) {
      models._sequence.set('permissions', models.permissions);
    }
    this.lineRanges = null;
    this.handleCanEdit = options.handleCanEdit;
    this.isInSync = options.isInSync;
    this.rotationIndex = options.rotationIndex;
    if (options.trimStart != null && options.trimEnd != null) {
      this.set({trim: new Trim({start: options.trimStart, end: options.trimEnd, sequence: this})});
    }
    this.registerListeners();
  }

  stopAllListeners(): void {
    this.stopListening();
  }

  registerListeners(): void {
    this.stopListening();
    this.listenTo(this.get('_sequence'), 'invalidateLineRanges', () => {
      this.invalidateLineRanges();
    });
    this.listenTo(this.get('_sequence'), 'stopAllListeners', () => {
      this.stopAllListeners();
    });

    const baseSequence = this.get('baseSequence');

    if (baseSequence != null) {
      this.listenTo(baseSequence.get('_sequence'), 'change:name', (model: Sequence, value: string): void => {
        if (this.isInSync()) {
          // Rename reference row name when the underlying sequence name changes and the alignment is in-sync.
          this.get('_sequence').set('name', value);
        }
      });
      this.listenTo(this.get('selection'), 'change:start change:end', () => {
        // Don't bother mimicking the selection if out-of-sync or the sequence is on an old version
        if (!(this.isInSync() && baseSequence.get('currentVersion'))) {
          return;
        }
        const alignmentSelection = this.get('selection');
        const gapSequence = this.get('_sequence');
        const start = gapSequence.gapToReal(alignmentSelection.start());
        assertAndContinue(start != null, 'Alignment gap to real not working', {
          start: alignmentSelection.start(),
          end: alignmentSelection.end(),
          baseLength: baseSequence.length(),
          gapLength: gapSequence.length(),
        });

        if (gapSequence.gapToReal(gapSequence.length()) !== baseSequence.length()) {
          // Although `this.isInSync()` returned true, the ungapped length unexpectedly does not match the
          // reference sequence length, so skip mirroring the selection.
          return;
        }
        // Reference sequence is guaranteed to not be reversed, but it may be rotated. Account
        // for that before mirroring the selection.
        const rotationAdjustedStart = sequtil.mod(start + this.rotationIndex, baseSequence.length());
        if (alignmentSelection.isNotEmpty()) {
          const end = gapSequence.gapToReal(alignmentSelection.end());
          const rotationAdjustedEnd = sequtil.mod(end + this.rotationIndex, baseSequence.length());

          if (start === end && alignmentSelection.start() < alignmentSelection.end()) {
            // Special case: the selection is non-empty, but it contains only gaps (i.e. the "real" indices for
            // start and end are the same)
            baseSequence.get('selection').reset(rotationAdjustedStart);
          } else {
            baseSequence.get('selection').setEndpoints(rotationAdjustedStart, rotationAdjustedEnd);
          }
        } else {
          baseSequence.get('selection').reset(rotationAdjustedStart);
        }
      });
    }
    this.listenToTranslations();
  }

  fileState(): FileState {
    if (this.get('baseSequence') != null) {
      return this.get('baseSequence')!.fileState();
    } else {
      // @ts-expect-error: This appears to dangerously break the BaseFile contract.
      return undefined;
    }
  }

  insert(
    position: number,
    bases: string,
    options: {
      callback?: () => void;
      parts?: Array<PartAttributes>;
      annotations?: Array<AnnotationAttributes>;
      translations?: Array<TranslationAttributes>;
      sequencePrimers?: Array<SequencePrimerAttributes>;
      modifiedNucleotides?: Array<ModifiedNucleotideAttributes>;
    } = {}
  ): DoSeqOpReturnPromise {
    if (options == null) {
      options = {};
    }
    // Although we display features on an alignment, we do not want to include new ones when
    // we insert bases. For example, we might have features from the clipboard here when pasting.
    delete options.annotations;
    delete options.translations;
    delete options.sequencePrimers;

    const xhr = (() => {
      if (!this.handleCanEdit()) {
        // handleCanEdit will already show a notification.
        return $.Deferred().reject({__suppress_notification: true}).promise();
      } else {
        const sanitizedBases = biojs.util.sanitize(bases, this.nucleotideType());
        const insertPromise =
          sanitizedBases.length === 0 || this.get('baseSequence') == null
            ? $.Deferred().resolve().promise()
            : this.get('baseSequence')!.insert(
                this.get('_sequence').gapToReal(position),
                sanitizedBases,
                options
              );
        return insertPromise.done(() => {
          this.get('_sequence').trigger('insert', position, bases, options);
        });
      }
    })();
    return this.wrapForSequenceXHR(xhr);
  }

  canInsertPart(): boolean {
    return false;
  }

  toggleCase(
    start: number,
    end: number,
    casing: Casing,
    options: {callback?: () => void} = {}
  ): DoSeqOpReturnPromise {
    if (options == null) {
      options = {};
    }
    let xhr;
    if (this.handleCanEdit()) {
      const baseSequence = this.get('baseSequence');
      const rStart = this.get('_sequence').gapToReal(start);
      const rEnd = this.get('_sequence').gapToReal(end);

      if (baseSequence) {
        xhr = baseSequence.toggleCase(rStart, rEnd, casing, options);
      } else {
        xhr = $.Deferred().resolve().promise();
      }
      xhr.done(() => {
        this.get('_sequence').trigger('toggleCase', start, end, casing, options);
      });
    } else {
      xhr = $.Deferred().reject({__suppress_notification: true}).promise();
    }

    return this.wrapForSequenceXHR(xhr);
  }

  deleteBases(
    start: number,
    end: number,
    options: {respondDiff?: boolean; fileUpdate?: boolean; patch?: boolean; callback?: () => void} = {}
  ): DoSeqOpReturnPromise {
    if (options == null) {
      options = {};
    }
    let xhr;
    if (this.handleCanEdit()) {
      const baseSequence = this.get('baseSequence');
      const selection = this.get('selection');
      if (!baseSequence) {
        xhr = $.Deferred().resolve().promise();
      } else {
        const rStart = this.get('_sequence').gapToReal(start);
        const rEnd = this.get('_sequence').gapToReal(end);
        xhr = baseSequence.deleteBases(rStart, rEnd, {});
      }
      xhr.done(() => {
        this.get('_sequence').trigger('delete', start, end, options);
        selection.reset(start);
      });
    } else {
      // handleCanEdit will already show a notification.
      xhr = $.Deferred().reject({__suppress_notification: true}).promise();
    }
    return this.wrapForSequenceXHR(xhr);
  }

  deleteSelection(
    options: {respondDiff?: boolean; fileUpdate?: boolean; patch?: boolean} = {}
  ): DoSeqOpReturnPromise {
    if (options == null) {
      options = {};
    }
    let xhr;
    if (this.handleCanEdit()) {
      const baseSequence = this.get('baseSequence');
      const selection = this.get('selection');
      const start = selection.start();
      const end = selection.end();
      if (baseSequence) {
        xhr = baseSequence.deleteSelection({});
      }
      if (!xhr) {
        xhr = $.Deferred().resolve().promise();
      }
      xhr.done(() => {
        this.get('_sequence').trigger('delete', start, end, options);
        selection.reset(selection.start());
      });
    } else {
      // handleCanEdit will already show a notification.
      return $.Deferred<void>().reject({__suppress_notification: true}).promise() as DoSeqOpReturnPromise;
    }
    return this.wrapForSequenceXHR(xhr);
  }

  replace(
    start: number,
    end: number,
    bases: string,
    options: {
      callback?: () => void;
      parts?: Array<PartAttributes>;
      annotations?: Array<AnnotationAttributes>;
      translations?: Array<TranslationAttributes>;
      sequencePrimers?: Array<SequencePrimerAttributes>;
      modifiedNucleotides?: Array<ModifiedNucleotideAttributes>;
      reverse?: boolean;
      complement?: boolean;
    } = {}
  ): DoSeqOpReturnPromise {
    if (options == null) {
      options = {};
    }
    // Although we display features on an alignment, we do not want to include new ones when
    // we replace bases. For example, we might have features from the clipboard here when pasting.
    delete options.annotations;
    delete options.translations;
    delete options.sequencePrimers;

    let xhr;
    if (this.handleCanEdit()) {
      const baseSequence = this.get('baseSequence');
      const rStart = this.get('_sequence').gapToReal(start);
      const rEnd = this.get('_sequence').gapToReal(end);
      const sanitizedBases = biojs.util.sanitize(bases, this.nucleotideType());
      if (baseSequence && (rStart !== rEnd || (rStart === rEnd && start >= end))) {
        if (sanitizedBases.length > 0) {
          xhr = baseSequence.replace(rStart, rEnd, sanitizedBases, options);
        } else {
          xhr = baseSequence.deleteBases(rStart, rEnd, options);
        }
      } else if (baseSequence && sanitizedBases.length > 0) {
        xhr = baseSequence.insert(rStart, sanitizedBases, options);
      } else {
        xhr = $.Deferred().resolve().promise();
      }
      xhr.done(() => {
        this.get('_sequence').trigger('replace', start, end, bases);
      });
    } else {
      // handleCanEdit will already show a notification.
      xhr = $.Deferred().reject({__suppress_notification: true}).promise();
    }
    return this.wrapForSequenceXHR(xhr);
  }

  createTranslation(
    strand: Strand,
    // Get around parent signature by shoving start & end in options.
    options: {callback?: () => void; bulk?: boolean; start: number; end: number | null} = {
      start: this.get('selection').start(),
      end: this.get('selection').end(),
    }
  ): Promise<void> {
    const deferred = $.Deferred<void>();
    const bulk = options.bulk != null && options.bulk;
    const {start, end} = options;
    if (this.handleCanEdit({isFeatureEdit: true})) {
      // Translations are not synced because completing deleting a translation does not sync correctly.
      // deleteBases for alignments use replaceBases, which will keep the translation around, while
      // the baseSequence will use deleteBases, which will remove the translation.
      this.get('_sequence').translations().add({start, end, strand});
      if (!bulk) {
        this.get('_sequence').trigger('save');
      }
      deferred.resolve();
    } else {
      // handleCanEdit will already show a notification.
      deferred.reject({__suppress_notification: true});
    }
    return Promise.resolve(deferred);
  }

  async createTranslationIfNew(
    strand: Strand,
    // Get around parent signature by shoving start & end in options.
    options: {start: number; end: number | null} = {
      start: this.get('selection').start(),
      end: this.get('selection').end(),
    }
  ): Promise<void> {
    const {start, end} = options;
    for (const translation of this.get('_sequence').translations().models) {
      if (translation.start() === start && translation.end() === end && translation.strand() === strand) {
        return;
      }
    }
    await this.createTranslation(strand, {start: options.start, end: options.end});
  }

  deleteFeature(feature: Annotation | Translation): Promise<void> {
    // Should only be used for annotations, translations
    const deferred: JQueryDeferred<void> = $.Deferred();
    if (this.handleCanEdit({isFeatureEdit: true})) {
      feature.destroy();
      this.get('_sequence').trigger('save');
      deferred.resolve();
    } else {
      // handleCanEdit will already show a notification.
      deferred.reject({__suppress_notification: true});
    }
    return Promise.resolve(deferred);
  }

  deleteTranslationGroup(groupId: number): JQueryPromise<void> {
    // Translation groups in alignment sequences aren't saved to the DB and have fake IDs, so they
    // need to be deleted by updating the sequence model and saving the alignment JSON
    // rather than through the normal translation group deletion endpoint.
    const deferred: JQueryDeferred<void> = $.Deferred();
    if (this.handleCanEdit({isFeatureEdit: true})) {
      const biojsGapSequence = this.get('_sequence');
      const translations = biojsGapSequence.translations().filter((t) => t.translationGroupId() === groupId);
      biojsGapSequence.translations().remove(translations);
      const newGroups = this.translationGroups().filter(({id}) => groupId !== id);
      biojsGapSequence.set({translationGroups: newGroups});
      biojsGapSequence.trigger('save');
      deferred.resolve();
    } else {
      // handleCanEdit will already show a notification.
      deferred.reject({__suppress_notification: true});
    }
    return deferred.promise();
  }

  makeEnzymes(): Enzymes {
    return new Enzymes();
  }

  updateORFs(): void {}

  updateCuts(): void {}

  getNoteParts(): Array<ServerNotePart> | undefined {
    return this.get('baseSequence')?.get('_sequence')?.get('note')?.noteParts;
  }

  // No-op syncing to GQL for now - GapSequenceWrappers don't have all the attributes normal Sequences do, so
  // don't sync until we add a GapSequenceWrapper GQL type
  initGraphQLSync(): void {}
}

export default GapSequenceWrapper;
